import { useEffect, useRef, useCallback } from "react";
import { MessageSquare, Trash2 } from "lucide-react";
import { useChat } from "../../hooks/useChat";
import { useVoiceChat } from "../../hooks/useVoiceChat";
import ChatMessage from "./ChatMessage";
import ChatInput from "./ChatInput";
import { VoiceButton } from "./VoiceButton";
import { AudioLevelIndicator } from "./AudioLevelIndicator";
import LoadingSpinner from "../ui/LoadingSpinner";

const SUGGESTED_PROMPTS = [
  "What are today's NFL scores?",
  "Who's winning the NBA game right now?",
  "When do the Yankees play next?",
  "Show me the NFL standings",
];

export default function ChatInterface() {
  const { messages, sendMessage, clearChat, isLoading } = useChat();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageCountRef = useRef(messages.length);

  // Voice chat integration
  const handleTranscription = useCallback(
    (text: string) => {
      sendMessage(text);
    },
    [sendMessage],
  );

  const voice = useVoiceChat({
    transcriptionHint: "Sports scores, NFL, NBA, MLB, NHL, MLS, teams, players",
    onTranscription: handleTranscription,
    onError: (error) => {
      console.error("Voice error:", error);
    },
  });

  // When chat starts loading, set voice state to thinking
  useEffect(() => {
    if (isLoading && voice.isActive) {
      voice.setThinking();
    }
  }, [isLoading, voice.isActive, voice.setThinking]);

  // When a new assistant message arrives, speak it if voice is active
  useEffect(() => {
    if (messages.length > lastMessageCountRef.current) {
      const lastMessage = messages[messages.length - 1];
      if (
        lastMessage.role === "ASSISTANT" &&
        voice.isActive &&
        voice.mode.output === "voice"
      ) {
        voice.speakResponse(lastMessage.content);
      }
    }
    lastMessageCountRef.current = messages.length;
  }, [messages, voice.isActive, voice.mode.output, voice.speakResponse]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSuggestedPrompt = (prompt: string) => {
    sendMessage(prompt);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-dark-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-primary-400" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-text-primary">
              Sports Talk
            </h1>
            <p className="text-sm text-text-muted">
              Ask me anything about sports
            </p>
          </div>
        </div>
        {messages.length > 0 && (
          <button
            onClick={clearChat}
            className="btn-secondary flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Clear
          </button>
        )}
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto py-6">
        {messages.length === 0 ? (
          <EmptyState onPromptClick={handleSuggestedPrompt} />
        ) : (
          <div className="space-y-6">
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}
            {isLoading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-dark-card flex items-center justify-center flex-shrink-0">
                  <LoadingSpinner size="sm" />
                </div>
                <div className="bg-dark-card border border-dark-border rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex items-center gap-2 text-sm text-text-muted">
                    <LoadingSpinner size="sm" />
                    <span>Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="pt-4 border-t border-dark-border">
        {/* Audio level indicator when listening */}
        {voice.state === "listening" && (
          <AudioLevelIndicator
            level={voice.audioLevel}
            isActive={voice.isActive}
            className="mb-3"
          />
        )}

        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <ChatInput
              onSend={sendMessage}
              disabled={isLoading || voice.state === "listening"}
            />
          </div>
          <VoiceButton
            state={voice.state}
            isActive={voice.isActive}
            mode={voice.mode}
            onClick={voice.toggle}
            className="h-12"
          />
        </div>

        {/* Voice error display */}
        {voice.error && (
          <p className="text-sm text-accent-red mt-2">{voice.error}</p>
        )}
      </div>
    </div>
  );
}

interface EmptyStateProps {
  onPromptClick: (prompt: string) => void;
}

function EmptyState({ onPromptClick }: EmptyStateProps) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-4">
      <div className="w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center mb-6">
        <MessageSquare className="w-8 h-8 text-primary-400" />
      </div>
      <h2 className="text-xl font-semibold text-text-primary mb-2">
        Welcome to Sports Talk
      </h2>
      <p className="text-text-muted mb-8 max-w-md">
        Get real-time scores, schedules, standings, and analysis for NFL, NBA,
        MLB, NHL, and MLS.
      </p>
      <div className="w-full max-w-md">
        <p className="text-sm font-medium text-text-secondary mb-3">
          Try asking:
        </p>
        <div className="grid gap-2">
          {SUGGESTED_PROMPTS.map((prompt) => (
            <button
              key={prompt}
              onClick={() => onPromptClick(prompt)}
              className="w-full text-left px-4 py-3 rounded-lg border border-dark-border bg-dark-card hover:bg-dark-hover hover:border-primary-500/50 transition-colors text-sm text-text-secondary"
            >
              {prompt}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
